import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import ArrowRight from "/public/images/icons/arrowRight.svg?react";
import { useThemeContext } from "../../context/themeContext";
import { useDrawerContext } from "../../context/drawerContext";
import Button from "../Button";
import CloseIcon from "/public/images/icons/close.svg?react";
import { format } from "date-fns";

export default function ActivityCard({
  activity,
  href,
  background,
  timeline,
  inView,
  dataPoints,
  subscription
}) {
  const { theme } = useThemeContext();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const handleDrawerToggle = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            UPGRADE
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col gap-2 justify-stretch">
          <h3 className="text-[24px]">Upgrade subscription</h3>
          <p className="text-sportsDarkGrey mb-[26px]">
            Based on your <b>bronze</b> subscription level, you can{" "}
            <b>only view</b> activities. To manually update your activities,
            please upgrade your subscription.
          </p>
          <Link to="/upgrade" onClick={() => setOpen(false)}>
            <Button className="w-full">Upgrade My Subscription</Button>
          </Link>
        </div>
      </div>
    );
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    const remainingMinutes = minutes % 60;
    const remainingSeconds = seconds % 60;
  
    if (days > 0) {
      return `${days} ${days === 1 ? 'day' : 'days'}${remainingHours > 0 ? ` ${remainingHours} ${remainingHours === 1 ? 'hour' : 'hours'}` : ''}${remainingMinutes > 0 ? ` ${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}` : ''}${remainingSeconds > 0 ? ` ${remainingSeconds} ${remainingSeconds === 1 ? 'second' : 'seconds'}` : ''}`;
    } else if (hours > 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}${remainingMinutes > 0 ? ` ${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}` : ''}${remainingSeconds > 0 ? ` ${remainingSeconds} ${remainingSeconds === 1 ? 'second' : 'seconds'}` : ''}`;
    } else if (minutes > 0) {
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}${remainingSeconds > 0 ? ` ${remainingSeconds} ${remainingSeconds === 1 ? 'second' : 'seconds'}` : ''}`;
    } else {
      return `${remainingSeconds} ${remainingSeconds === 1 ? 'second' : 'seconds'}`;
    }
  };
  
  const CardContent = () => (
    <div
      style={{ background }}
      className={`border-[1px] border-[#E3E3E3] rounded-[4px] p-6 flex flex-col gap-5`}
    >
      <div
        className={`${
          theme === "staff" ? "text-sportsBlue" : "text-sportsYellow"
        } font-semibold leading-6 tracking-widest`}
      >
        {format(new Date(activity?.whenHappened), "dd/MM/yyyy • HH:mm")}
      </div>
      <div className="space-y-1">
        {/* Top Block */}
        <div className="leading-[34px] text-[24px] font-semibold antialiased capitalize">
          {activity?.orgPartnerFriendlyName || activity?.location}
        </div>
        <div className="leading-6 font-semibold capitalize flex gap-2">
          <img
            className="w-6 h-6"
            src={activity.activityIcon}
            alt={activity.activityName}
          />
          {activity.activityName}
        </div>
        <div className="text-sportsDarkGrey leading-6">
          {formatDuration(activity?.duration)}
        </div>
        {activity.notes && (
          <div className="">
            <p className="font-semibold font-poppins text-[16px] leading-[26px] mt-[8px]">Note</p>
            <p className="text-sportsDarkGrey font-poppins text-[16px] leading-[26px]">{activity.notes}</p>
          </div>
        )}
      </div>
      
      <div className="dashed-border w-full h-0.5" />
        {(dataPoints && dataPoints.length > 0) ? (
          <div className="space-y-2">
            {dataPoints?.map((dataPoint, index) => {
              if(dataPoints.length === (index + 1)) {
                return (
                  <div className="flex justify-between" key={`${dataPoint.dataPointId}-${dataPoint.dataPointValue}`}>
                    <div className="leading-6 font-medium text-black flex gap-4" key={dataPoint.dataPointGUID}>
                      <img className="w-[24px] h-[24px]" src={dataPoint.dataPointIcon} alt={dataPoint.dataPointType} /> {dataPoint.dataPointType.split(" ")[0]} / {dataPoint?.dataPointPrefix + dataPoint?.dataPointValue + dataPoint?.dataPointSuffix}
                    </div>
                    <ArrowRight />
                  </div>
                )
              }
              return (
                <div className="leading-6 font-medium text-black flex gap-4" key={`${dataPoint.dataPointId}-${dataPoint.dataPointValue}`}>
                  <img className="w-[24px] h-[24px]" src={dataPoint.dataPointIcon} alt={dataPoint.dataPointType} /> {dataPoint.dataPointType.split(" ")[0]} / {dataPoint?.dataPointPrefix + dataPoint?.dataPointValue + dataPoint?.dataPointSuffix}
                </div>
              )
            })}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex justify-end">
              <ArrowRight />
            </div>
          </div>
        )}
    </div>
  );

  return (
    <div className={`activity-card relative activityID${activity.activityId}`}>
      {timeline && (
        <div
          className={`absolute left-[-36px] top-[3px] ${
            inView
              ? "bg-sportsYellow/20"
              : theme === "staff"
              ? "bg-sportsBlue"
              : "bg-sportsYellow"
          } rounded-full flex items-center justify-center w-[10px] h-[10px] mx-[3px]`}
        >
          {inView && (
            <div
              className={`w-[10px] h-[10px] rounded-full relative ${
                theme === "staff" ? "bg-sportsBlue" : "bg-sportsYellow"
              }`}
            >
              <div
                className={`w-[10px] h-[10px] rounded-full absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 animate-activity-scale ${
                  theme === "staff" ? "bg-sportsBlue/20" : "bg-sportsYellow/20"
                }`}
              />
            </div>
          )}
        </div>
      )}

      {subscription !== "Bronze" ? (
        <Link role="link" data-testid='activityCard' to={href || '#'} state={{ activity }}>
          <CardContent />
        </Link>
      ) : (
        <div data-testid='activityCard' onClick={handleDrawerToggle}>
          <CardContent />
        </div>
      )}
    </div>
  );
}

ActivityCard.propTypes = {
  href: PropTypes.string,
  activity: PropTypes.shape({
    whenHappened: PropTypes.string.isRequired,
    activityName: PropTypes.string.isRequired,
    activityId: PropTypes.string.isRequired,
    activityIcon: PropTypes.string.isRequired,
    orgPartnerFriendlyName: PropTypes.string.isRequired,
    duration: PropTypes.number.isRequired,
    dataPoints: PropTypes.arrayOf(
      PropTypes.shape({
        dataPointIcon: PropTypes.string,
        dataPointId: PropTypes.string,
        dataPointPrefix: PropTypes,
        dataPointSuffix: PropTypes,
        dataPointType: PropTypes.string,
        dataPointValue: PropTypes.string,
      })
    ),
  }).isRequired,
  background: PropTypes.string,
  timeline: PropTypes.bool,
  inView: PropTypes.bool,
  subscription: PropTypes.string,
};
